import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Building2, Mail, Lock, Eye, EyeOff, ArrowRight, Check, Box, ArrowLeft } from "lucide-react";
import { API } from "../../lib/auth";

export default function Register() {
  const nav = useNavigate();
  const [step] = useState(1);
  const [showPw, setShowPw] = useState(false);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    subdomain: "", company_name: "", admin_email: "", password: "", confirm: "",
    plan_id: "plan_pro", billing_cycle: "monthly",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const selectedPlan = plans.find((p) => p.id === form.plan_id);
  const price = selectedPlan ? (form.billing_cycle === "monthly" ? selectedPlan.price_monthly : selectedPlan.price_yearly) : 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.subdomain.match(/^[a-z0-9-]{3,30}$/)) return toast.error("Subdomain must be 3-30 lowercase letters/numbers/hyphens");
    if (!form.company_name) return toast.error("Company name is required");
    if (!form.admin_email.includes("@")) return toast.error("Valid email required");
    if (form.password.length < 8) return toast.error("Password must be 8+ characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register-tenant`, {
        subdomain: form.subdomain, company_name: form.company_name,
        admin_email: form.admin_email, password: form.password,
        plan_id: form.plan_id, billing_cycle: form.billing_cycle,
      });
      toast.success("Workspace created! Continue to payment.");
      nav(`/payment/${data.tenant_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-page" className="min-h-screen grid lg:grid-cols-2 bg-[#FFFCF2]">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-[#1B4332] via-[#2D6A4F] to-[#52B788] text-white p-12 flex-col justify-between">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-10 -left-10 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back to brivo360.com
          </Link>
        </div>
        <div className="relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-8">
            <Box size={32} />
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Launch your business<br />in minutes
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-md leading-relaxed">
            Get your own dedicated workspace with a custom subdomain, full inventory management, and everything you need to run your business.
          </p>
          <ul className="mt-10 space-y-4">
            {["Dedicated subdomain & database", "POS, inventory, purchases & sales", "Multi-warehouse & barcode support", "Ready in under 60 seconds"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center mt-0.5"><Check size={14} /></span>
                <span className="text-white/90">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs text-white/50">© Brivo360. 2026</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-10 lg:p-14 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-7">
          <img 
              src="https://brivo360.com/images/brivo-logo2.png" 
              alt="Brivo360" 
              className="h-10 w-auto cursor-pointer"
            />
          </div>

          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black tracking-tight">Create your business</h2>
          <p className="mt-2 text-black/60">Set up your subdomain and admin account to get started.</p>

          {/* Stepper */}
          <div className="mt-6 grid grid-cols-3 gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 rounded-full transition-colors ${s <= step ? "bg-gradient-to-r from-[#1FA868] to-[#52B788]" : "bg-black/10"}`} />
            ))}
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Subdomain</label>
              <div className="flex items-stretch rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white overflow-hidden">
                <span className="flex items-center pl-4 pr-2 text-black/40"><Globe size={16} /></span>
                <input
                  data-testid="register-subdomain"
                  value={form.subdomain}
                  onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase() })}
                  placeholder="your-company"
                  className="flex-1 px-2 py-3 focus:outline-none bg-white"
                />
                <span className="flex items-center px-3 bg-[#FFFCF2] text-black/60 text-sm border-l border-black/10">.brivopos.com</span>
              </div>
              <p className="text-xs text-black/50 mt-1">Lowercase letters, numbers, and hyphens only.</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Company name</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Building2 size={16} className="text-black/40" />
                <input data-testid="register-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Acme Inc." className="flex-1 px-3 py-3 focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Admin email</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Mail size={16} className="text-black/40" />
                <input data-testid="register-email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  placeholder="info@best.com" className="flex-1 px-3 py-3 focus:outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-black mb-1.5 block">Password</label>
                <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                  <Lock size={16} className="text-black/40" />
                  <input data-testid="register-password" type={showPw ? "text" : "password"} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Min. 8 chars" className="flex-1 px-3 py-3 focus:outline-none" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="text-black/40">
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-black mb-1.5 block">Confirm</label>
                <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                  <Check size={16} className="text-black/40" />
                  <input data-testid="register-confirm" type="password" value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    placeholder="Repeat" className="flex-1 px-3 py-3 focus:outline-none" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Plan</label>
              <select data-testid="register-plan" value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-black/15 focus:border-[#1B4332] focus:outline-none bg-white">
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — ₦{p.price_monthly.toLocaleString()}.00/mo</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Billing cycle</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: "monthly", lbl: "Monthly", val: selectedPlan?.price_monthly || 0, sfx: "/mo" },
                  { k: "yearly", lbl: "Yearly", val: selectedPlan?.price_yearly || 0, sfx: "/yr" },
                ].map((b) => (
                  <button key={b.k} type="button" data-testid={`register-cycle-${b.k}`}
                    onClick={() => setForm({ ...form, billing_cycle: b.k })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${form.billing_cycle === b.k ? "border-[#1FA868] bg-[#E8F5E9]" : "border-black/10 bg-white hover:border-black/20"}`}>
                    <div className="font-semibold text-black">{b.lbl}</div>
                    <div className="text-xs text-black/60 mt-1">₦{b.val.toLocaleString()}.00{b.sfx}</div>
                  </button>
                ))}
              </div>
            </div>

            <button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full mt-3 py-3.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-95 transition-opacity disabled:opacity-60">
              {loading ? "Creating..." : "Create workspace"} <ArrowRight size={16} />
            </button>

            <Link to="/" className="block text-center text-sm text-black/60 hover:text-black mt-2">
              ← Back to homepage
            </Link>
            <p className="text-xs text-black/50 text-center mt-2">
              Already have account? <a href="/login" className="text-[#1B4332] font-semibold">Login</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
